import {
  useMutation,
  useQueryClient,
  type MutateOptions,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { gqlClient } from '@/lib';
import type { CreateChatInput } from '@/types/gql.generated';
import { getLocalTimeZone } from '@/utils/dates';
import type { QueryError } from '@/utils/errors';
import { createGetMagicChatQueryKey } from '@/utils/queryKeys';
import {
  useChatContext,
  useChatInternal,
} from '../contexts/ChatContext/useChatContext';
import { ChatFragment } from '../fragments/ChatFragment';
import type {
  CreateChatMutation,
  CreateChatMutationVariables,
} from './useCreateChat.generated';

const query = gql`
  ${ChatFragment}
  mutation CreateChat($input: CreateChatInput!) {
    createChat(input: $input) {
      ...MagicChat
    }
  }
`;

export const useCreateChat = () => {
  const queryClient = useQueryClient();
  const { timeZone } = useChatContext();
  const { markChatPersisted } = useChatInternal();
  const { mutate, ...rest } = useMutation<
    CreateChatMutation,
    QueryError,
    CreateChatMutationVariables
  >({
    mutationFn: async (variables) => {
      return gqlClient.request<CreateChatMutation, CreateChatMutationVariables>(
        query,
        variables
      );
    },
  });

  const createChat = useCallback(
    (
      data: Partial<CreateChatInput>,
      options: MutateOptions<
        CreateChatMutation,
        QueryError,
        CreateChatMutationVariables
      > = {}
    ) => {
      const input = {
        timeZone: timeZone ?? getLocalTimeZone(),
        ...data,
      };

      return mutate(
        { input },
        {
          ...options,
          onSuccess: (...args) => {
            const [result] = args;
            const chat = result.createChat;
            // The server now has this row, whatever id it settled on — record
            // it so nothing tries to create it a second time.
            markChatPersisted(String(chat.id));
            queryClient.setQueriesData(
              {
                queryKey: createGetMagicChatQueryKey(String(chat.id)),
              },
              {
                getChat: { ...chat, forceChange: true },
              }
            );
            options.onSuccess?.(...args);
          },
        }
      );
    },
    [queryClient, mutate, markChatPersisted, timeZone]
  );

  return {
    createChat,
    ...rest,
  };
};
